import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, User, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { API_URL as BACKEND_URL } from '../config';
import { safeFetch } from '../utils/safeFetch';
import { haptic } from '../utils/vibration';
import useToast from '../hooks/useToast';
import { getClientVersion, getClientVersionHeaders } from '../utils/clientVersion';
import { clearAllOfflineRoutesCache } from '../utils/offlineRoutesCache';
import FloatingField from '../components/FloatingField';

const API_AUTH_URL = BACKEND_URL + '/api/login';
const API_TELEGRAM_AUTH_URL = BACKEND_URL + '/api/telegram-auth';

const Auth = ({ onAuth, isLoggingOut = false }) => {
  const navigate = useNavigate();
  const [isTelegram, setIsTelegram] = useState(false);
  const [loading, setLoading] = useState(false);
  const [login, setLogin] = useState(() => localStorage.getItem('last_login') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    const authNotice = sessionStorage.getItem('auth_notice');
    if (!authNotice) return;

    sessionStorage.removeItem('auth_notice');
    if (authNotice === 'user-blocked') {
      showToast('Учетная запись заблокирована администратором. Локальные данные удалены.', 'error');
    } else if (authNotice === 'offline-expired') {
      showToast('Срок действия офлайн-данных истёк. Для продолжения войдите в систему с интернетом.', 'warning');
    } else {
      showToast('Сессия завершена. Войдите в приложение снова.', 'info');
    }
  }, [showToast]);

  useEffect(() => {
    // ОТКЛЮЧЕНО ПО ЗАПРОСУ ПОЛЬЗОВАТЕЛЯ
    /*
    if (isLoggingOut) {
      setIsTelegram(false);
      return;
    }

    // Проверяем Telegram WebApp и наличие initData и chat_id
    const tg = window.Telegram && window.Telegram.WebApp;
    const init_data = tg?.initData;
    const chat_id = tg?.initDataUnsafe?.user?.id;
    if (tg && init_data && chat_id) {
      setIsTelegram(true);
      handleTelegramAuth(init_data, chat_id);
    } else {
      setIsTelegram(false);
    }
    */
  }, [isLoggingOut]);

  useEffect(() => {
    if (isLoggingOut) {
      setPassword('');
      setShowPassword(false);
      setLogin(localStorage.getItem('last_login') || '');
    }
  }, [isLoggingOut]);

  // Авторизация через Telegram
  const handleTelegramAuth = async (init_data, chat_id) => {
    setLoading(true);
    try {
      const data = await safeFetch(API_TELEGRAM_AUTH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ init_data, chat_id })
      });
      
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user_type', data.user_type);
      if (onAuth) onAuth(data);
      navigate('/my-orders', { replace: true });
    } catch (e) {
      showToast(e.message || 'Ошибка авторизации', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Форма логина/пароля
  const handleLogin = async (e) => {
    e.preventDefault();
    haptic('selection');
    setLoading(true);
    try {
      const response = await fetch(API_AUTH_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getClientVersionHeaders()
        },
        body: JSON.stringify({
          username: login,
          password: password,
          client_version: getClientVersion()
        })
      });
      const data = await response.json();
      if (!response.ok) {
        const msg = data?.detail || data?.message || '';
        if (response.status === 401 || msg.toLowerCase().includes('неверный') || msg.toLowerCase().includes('invalid')) {
          showToast('Неверный логин или пароль', 'error');
        } else if (response.status === 403 || msg.toLowerCase().includes('заблокирован') || msg.toLowerCase().includes('удален')) {
          clearAllOfflineRoutesCache().catch(() => {});
          showToast('Ваш аккаунт заблокирован. Обратитесь к администратору.', 'error');
        } else if (msg.toLowerCase().includes('попыток')) {
          showToast('Слишком много неудачных попыток. Попробуйте позже.', 'error');
        } else {
          showToast(msg || 'Ошибка авторизации', 'error');
        }
        return;
      }
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user_type', data.user_type);
      localStorage.setItem('last_login', login.trim());
      if (onAuth) onAuth(data);
      navigate('/my-orders', { replace: true });
    } catch (e) {
      showToast('Нет ответа от сервера. Проверьте подключение.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4 py-8 overflow-auto">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 space-y-6 my-auto">
        <div className="flex flex-col items-center mb-4">
          <LogIn className="w-10 h-10 text-blue-600 mb-2" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Авторизация</h2>
        </div>
        {isTelegram && loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        )}
        {isTelegram && loading ? (
          <div className="text-center text-gray-700 dark:text-gray-300">
            <p>Вход через Telegram...</p>
            <p className="text-xs text-gray-400 mt-2">Пожалуйста, дождитесь проверки авторизации</p>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleLogin}>
            <div>
              <FloatingField label="Логин" disabled={loading}>
                <div className="relative">
                  <input
                    type="text"
                    value={login}
                    onChange={e => setLogin(e.target.value)}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-colors bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 focus:ring-blue-500"
                    placeholder="Введите логин"
                    autoFocus
                    disabled={loading}
                  />
                  <User className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              </FloatingField>
            </div>
            <div>
              <FloatingField label="Пароль" disabled={loading}>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-20 border rounded-xl focus:outline-none focus:ring-2 transition-colors bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 focus:ring-blue-500"
                    placeholder="Введите пароль"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      haptic('tap');
                      setShowPassword(!showPassword);
                    }}
                    className="absolute right-10 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              </FloatingField>
            </div>
            <button
              type="submit"
              disabled={loading || !login || !password}
              onClick={() => {
                if (loading || !login || !password) haptic('info');
              }}
              className="w-full py-3 rounded-xl font-medium bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="inline-flex items-center justify-center gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loading ? 'Вход...' : 'Войти'}
              </span>
            </button>
          </form>
        )}
      </div>
          </div>
  );
};

export default Auth;
